// File: frontend/src/pages/CalendarPage.jsx

import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css'; // Import the calendar's styles
import { classesAPI } from '../services/api';
import Spinner from '../components/Spinner';

const eventStyleGetter = (event) => {
    const style = {
        backgroundColor: event.resource.teacher_color || '#3174ad', // Use teacher's color or a default
        borderRadius: '5px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
    };
    return { style };
};

// Set up the localizer by telling the calendar to use moment.js for dates
const localizer = momentLocalizer(moment);

// Helper function to convert our class data into calendar "events".
// Recurring classes repeat every week, so they need an event generated for
// EVERY occurrence within the visible window - not just the single next
// upcoming date - otherwise the month view (which spans ~6 weeks) only ever
// shows the one week that the "next occurrence" happened to land in.
const transformClassesToEvents = (classes, rangeStart, rangeEnd) => {
  const events = [];
  const dayMap = {
    'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
    'Thursday': 4, 'Friday': 5, 'Saturday': 6
  };

  classes.forEach(cls => {
    if (!cls.start_time || !cls.end_time) return;
    const [startHour, startMinute] = cls.start_time.split(':').map(Number);
    const [endHour, endMinute] = cls.end_time.split(':').map(Number);

    if (cls.is_one_off) {
      if (!cls.one_off_date) return;
      const oneOffDate = moment(cls.one_off_date);
      const startDate = oneOffDate.clone().hour(startHour).minute(startMinute).toDate();
      const endDate = oneOffDate.clone().hour(endHour).minute(endMinute).toDate();
      events.push({
        id: `${cls.id}_${oneOffDate.format('YYYY-MM-DD')}`,
        title: `${cls.name} (${cls.teacher_name || 'Unassigned'})`,
        start: startDate,
        end: endDate,
        resource: cls,
      });
      return;
    }

    const dayOfWeek = dayMap[cls.day];
    if (dayOfWeek === undefined) return; // Skip if day is invalid

    // Walk every week in the window, generating one event per occurrence.
    let cursor = rangeStart.clone().day(dayOfWeek);
    if (cursor.isBefore(rangeStart, 'day')) {
      cursor.add(1, 'week');
    }

    while (cursor.isSameOrBefore(rangeEnd, 'day')) {
      const startDate = cursor.clone().hour(startHour).minute(startMinute).toDate();
      const endDate = cursor.clone().hour(endHour).minute(endMinute).toDate();
      events.push({
        id: `${cls.id}_${cursor.format('YYYY-MM-DD')}`,
        title: `${cls.name} (${cls.teacher_name || 'Unassigned'})`,
        start: startDate,
        end: endDate,
        resource: cls, // Keep the original class object for future use (e.g., clicking on it)
      });
      cursor = cursor.clone().add(1, 'week');
    }
  });

  return events;
};


export default function CalendarPage() {
  const [classesData, setClassesData] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('week');

  useEffect(() => {
    setIsLoading(true);
    classesAPI.getAll()
      .then(data => {
        setClassesData(data);
      })
      .catch(err => {
        console.error("Failed to fetch classes for calendar:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Regenerate recurring occurrences for whatever window is currently
  // visible, so navigating months/weeks keeps showing the full series
  // instead of just the single "next occurrence" from page load.
  useEffect(() => {
    const current = moment(date);
    let rangeStart, rangeEnd;
    if (view === 'month') {
      rangeStart = current.clone().startOf('month').startOf('week').subtract(1, 'week');
      rangeEnd = current.clone().endOf('month').endOf('week').add(1, 'week');
    } else if (view === 'week') {
      rangeStart = current.clone().startOf('week').subtract(1, 'week');
      rangeEnd = current.clone().endOf('week').add(1, 'week');
    } else {
      rangeStart = current.clone().startOf('day').subtract(1, 'week');
      rangeEnd = current.clone().endOf('day').add(1, 'week');
    }
    setEvents(transformClassesToEvents(classesData, rangeStart, rangeEnd));
  }, [classesData, date, view]);

  if (isLoading) {
    return <div className="p-10"><Spinner /></div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Class Schedule</h2>
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow" style={{ height: '70vh' }}>
        {/* Month view's 7-column grid doesn't reflow below ~700px - rather
            than silently clipping Saturday's column off-screen (the mobile
            bug reported), this makes it an intentional, scrollable region
            with a visible scrollbar instead. */}
        <div className="overflow-x-auto h-full">
          <div style={{ minWidth: 700, height: '100%' }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              date={date}
              view={view}
              onNavigate={setDate}
              onView={setView}
              defaultView="week" // Start with the "week" view
              views={['month', 'week', 'day']} // Allow users to switch views
              style={{ height: '100%' }}
              eventPropGetter={eventStyleGetter}
            />
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 640px) {
          .rbc-toolbar { flex-direction: column; align-items: stretch; gap: 0.5rem; }
          .rbc-toolbar .rbc-btn-group { justify-content: center; }
          .rbc-toolbar button { padding: 0.35rem 0.6rem; font-size: 0.8rem; }
          .rbc-toolbar-label { font-size: 0.95rem; font-weight: 700; }
        }
      `}</style>
    </div>
  );
}